import React, { useContext, useEffect, useRef, useState } from 'react';
import Style from "./style.module.css";
import XCloseIcon from '../../icons/x-close';
import { capitalize } from '@mui/material';
import LeftSidePopupLayout from '../../popup/modal-layout';
import { GlobalContext } from '../../../context/Context';
import moment from 'moment/moment';
import { useApiCall } from '../../custom-hooks/api-call';
import Swal from 'sweetalert2'
import DeleteBinLineIcon from '../../icons/delete-bin-line';
import ChevronDownIcon from '../../icons/chevron-down';
import DataNotFound from '../../ui/loader/data-not-found';

const FilterTabsPopup = ({ onClose, onSave = () => { }, data, returnData, saveFilter, editingFilters, selectFilterName = "My Filter", apiUrl, saveFilterOff=false }) => {
  const [filters, setFilters] = useState([]);
  const [status, setStatus] = useState(false);
  const [departmentList, setDepartmentList] = useState([]);
  const { state } = useContext(GlobalContext);
  const apiCall = useApiCall();
  const [onSaveFilter, setOnSaveFilter] = useState(saveFilter);

  useEffect(() => {
    const initialized = data.map((fieldName) => {
      const existing = returnData?.find((f) => f.field === fieldName);

      return {
        field: fieldName,
        value:
          existing?.value ??
          (fieldName === 'search_by_salary' || fieldName === 'search_by_joining_date'
            ? ['', '']
            : fieldName === 'search_by_emp_status'
              ? ''
              : fieldName === 'search_by_emp_type'
                ? ''
                : ''),
        selectedOperator:
          existing?.selectedOperator ??
          ((fieldName === 'search_by_salary' || fieldName.includes('amount'))
            ? 'between'
            : (fieldName === 'search_by_joining_date' || fieldName === 'status')
              ? 'is'
              : 'contains'),
        isChecked: !!existing,
        is_visible: true,
      };
    });

    setFilters(initialized);
    setTimeout(() => setStatus(true), 50);
    setDepartmentList(state?.getDropdowns?.departmentList);
  }, [data, returnData]);

  const closeFun = () => {
    setStatus(false);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleCheckboxToggle = (index) => {
    const updated = [...filters];
    updated[index].isChecked = !updated[index].isChecked;
    setFilters(updated);
  };

  const handleInputChange = (index, e) => {
    const updated = [...filters];
    updated[index].value = e.target.value;
    setFilters(updated);
  };

  const handleSalaryChange = (index, pos, val) => {
    const updated = [...filters];
    const salaryRange = [...updated[index].value];
    salaryRange[pos] = val;
    updated[index].value = salaryRange;
    setFilters(updated);
  };

  const handleSubmit = () => {
    const formatted = filters
      .filter(f => f.isChecked)
      .map(f => ({
        field: f.field,
        value: f.field === 'search_by_salary' || f.field === 'search_by_joining_date' && f.selectedOperator === 'between'
          ? f.value.filter(v => v !== '')
          : f.value,
        selectedOperator: f.selectedOperator
      }))
      .filter(f => Array.isArray(f.value) ? f.value.length > 0 : f.value !== '');
    const filterName = saveFilterName;
    onSave(formatted, filterName, editingFilter);
    closeFun();
  };

  const [editingFilter, setEditingFilter] = useState(editingFilters);
  const [saveFilterName, setSaveFilterName] = useState(selectFilterName);
  const [saveFilterIs, setSaveFilterIs] = useState(false);
  const [originalFilters, setOriginalFilters] = useState([]);
  const wrapperRef = useRef(null);

  const handleSaveFilter = async () => {
    const formatted = filters
      .filter(f => f.isChecked)
      .map(f => ({
        field: f.field,
        value:
          (f.field === 'search_by_salary' || (f.field === 'search_by_joining_date' && f.selectedOperator === 'between'))
            ? f.value.filter(v => v !== '')
            : f.value,
        selectedOperator: f.selectedOperator
      }))
      .filter(f => Array.isArray(f.value) ? f.value.length > 0 : f.value !== '');

    if (formatted.length === 0) {
      Swal.fire('No filters selected', 'Please select and fill filters before saving.', 'warning');
      return;
    }

    // Original filter name if editing
    const originalName = editingFilter?.filterName || '';

    let confirmBtnText = editingFilter ? 'Update' : 'Save';

    const { value: filterName } = await Swal.fire({
      title: editingFilter ? 'Update filter name' : 'Enter filter name',
      input: 'text',
      inputValue: originalName,
      inputPlaceholder: 'Enter a name for this filter',
      showCancelButton: true,
      confirmButtonText: confirmBtnText,
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value.trim()) {
          return 'Filter name is required';
        }
      },
      didOpen: (popup) => {
        const input = popup.querySelector('input');
        const confirmBtn = popup.querySelector('.swal2-confirm');

        input.addEventListener('input', () => {
          if (editingFilter) {
            // Check if input differs from original
            if (input.value.trim() !== originalName.trim()) {
              confirmBtn.textContent = 'Duplicate';
              confirmBtnText = 'Duplicate';
            } else {
              confirmBtn.textContent = 'Update';
              confirmBtnText = 'Update';
            }
          }
        });
      }
    });

    if (!filterName) {
      return; // Cancelled or empty input
    }

    try {
      const apiData = {
        api_url: apiUrl,
        filter_name: filterName.trim(),
        filters_json: formatted,
      };

      // If editing, send filter_id too
      if (editingFilter?.filterId) {
        apiData.filter_id = editingFilter.filterId;
      }

      await apiCall({ method: 'post', url: 'list-config/save-filters', data: JSON.stringify(apiData) });

      Swal.fire('Success', `Filter ${confirmBtnText} successfully`, 'success');

      onSave(formatted, filterName.trim(), editingFilter);
      setEditingFilter(null);

    } catch (error) {
      console.warn('Error saving filter:', error);
      Swal.fire('Error', 'Failed to save filter. Please try again.', 'error');
    }
  };

  const handleDeletFilter = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "Do you want to delete this filter? This action cannot be undo.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      const apiData = JSON.stringify({ filter_id: id });
      await apiCall({ method: 'post', url: 'delete-user-filter', data: apiData });
      setOnSaveFilter(prevFilters => prevFilters.filter(f => f.id !== id));

      if (editingFilter?.filterId === id) {
        setEditingFilter(null);  // reset editingFilter if it was deleted
        const reset = filters.map(f => ({
          ...f,
          isChecked: false,
          value:
            f.field === 'search_by_salary' || f.field === 'search_by_joining_date'
              ? ['', '']
              : '',
          selectedOperator:
            f.field === 'search_by_salary'
              ? 'between'
              : f.field === 'search_by_joining_date'
                ? 'is'
                : 'contains',
        }));
        setFilters(reset);
        setSaveFilterName('My Filter');
      }
      Swal.fire('Deleted!', 'Your filter has been deleted.', 'success');
    } catch (error) {
      console.warn("Error deleting filter:", error);
      Swal.fire('Error', 'Failed to delete filter. Please try again.', 'error');
    }
  };

  const handleSaveFilterChange = (rdata, filterName, filterId) => {
    const initialized = data.map((fieldName) => {
      const existing = rdata?.find((f) => f.field === fieldName);

      return {
        field: fieldName,
        value:
          existing?.value ??
          (fieldName === 'search_by_salary' || fieldName === 'search_by_joining_date'
            ? ['', '']
            : ''),
        selectedOperator:
          existing?.selectedOperator ??
          (fieldName === 'search_by_salary'
            ? 'between'
            : fieldName === 'search_by_joining_date'
              ? 'is'
              : 'contains'),
        isChecked: !!existing,
        is_visible: true,
      };
    });
    setFilters(initialized);
    setOriginalFilters(JSON.parse(JSON.stringify(initialized))); // deep copy
    setEditingFilter({ filterName, filterId });
    setSaveFilterName(filterName);
  };

  // Compare function to check if filters changed
  const isFiltersChanged = () => {
    return JSON.stringify(filters) !== JSON.stringify(originalFilters);
  };


  // Close dropdown if clicked outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setSaveFilterIs(false);
      }
    }

    if (saveFilterIs) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [saveFilterIs]);

  return (
    <LeftSidePopupLayout popupStatus={status} className={Style.customize_modal}>
      <div className={Style.modal_header}>
        <h4>Table Filters</h4>
        <button onClick={closeFun} className={Style.close_btn}><XCloseIcon /></button>
      </div>

      <div className={`${Style.modal_body} ${Style.filterBodyPopup}`}>
        {
          !saveFilterOff && (
            <div className={Style.myFilter}>
              <div ref={wrapperRef} className={`${Style.myFilter_main} ${saveFilterIs ? Style.active : ''}`}>
                <h5 onClick={() => { setSaveFilterIs(!saveFilterIs) }}>
                  {saveFilterName}

                  <div className={Style.dropIcon}>
                    <ChevronDownIcon />
                  </div>
                </h5>

                {
                  saveFilterIs && (
                    <div className={Style.myFilter__wrap}>
                      {
                        onSaveFilter?.length ? (
                          onSaveFilter.map((ele, idx) => (
                            <div
                              className={`${Style.myFilter__items} ${(saveFilterName == ele?.filter_name) ? Style.isActive : ''}`}
                              key={ele.id || idx}
                              onClick={() => { handleSaveFilterChange(ele?.config_filters, ele?.filter_name, ele?.id); setSaveFilterIs(false) }}
                            >
                              <p>
                                {ele.filter_name}
                              </p>

                              <div className={Style.myFilter__bin} onClick={(e) => { e.stopPropagation(); handleDeletFilter(ele?.id); setSaveFilterIs(false) }}>
                                <div className={Style.icon}>
                                  <DeleteBinLineIcon />
                                </div>
                              </div>
                            </div>
                          ))
                        ) : <div
                          className={Style.myFilter__items}
                        >
                          <DataNotFound title={'Filter'} className={Style.noDataFound} />
                        </div>
                      }
                    </div>
                  )
                }
              </div>

              <button
                className={`${Style.myFilter_btn}`}
                onClick={handleSaveFilter}
                disabled={
                  editingFilter
                    ? !isFiltersChanged() // disable if no changes
                    : filters.filter(f =>
                      f.isChecked &&
                      (f.field === 'search_by_salary'
                        ? f.value[0] || f.value[1]
                        : f.value)
                    ).length === 0
                }
              >
                {editingFilter ? 'Update Filter' : 'Save Filter'}
              </button>
            </div>
          )
        }


        <h6 className={`${(saveFilterOff) ? 'pt-4' : ''}`}>Search By:</h6>
        {filters.map((filter, index) => (
          filter.is_visible && (
            <div className={Style.filter_item} key={index}>
              <div className={Style.filter_label}>
                <label>
                  <input
                    type="checkbox"
                    checked={filter.isChecked}
                    onChange={() => handleCheckboxToggle(index)}
                  />
                  {
                    (filter.field === 'month_year') ? (
                      'Date'
                    ) : (
                      capitalize(filter.field.replace(/search_by_/g, '').replace(/emp/g, 'Employee').replace(/_/g, ' '))
                    )
                  }
                </label>
              </div>

              {filter.isChecked && (
                <div className={Style.filter_OprationAera}>

                  {filter.field === 'search_by_salary' ? (
                    <>
                      <div className={Style.filter_input}>
                        <select
                          value={filter.selectedOperator}
                          onChange={(e) => {
                            const updated = [...filters];
                            updated[index].selectedOperator = e.target.value;
                            if (e.target.value === 'between') {
                              updated[index].value = ['', '']; // reset for range inputs
                            } else {
                              updated[index].value = ''; // single value input
                            }
                            setFilters(updated);
                          }}
                        >
                          <option value="is" disabled>is</option>
                          <option value="contains" disabled>contains</option>
                          <option value="between">between</option>
                          <option value="start_with" disabled>Start with</option>
                          <option value="end_with" disabled>End with</option>
                        </select>
                      </div>
                      <div className={Style.date_range_wrapper}>
                        <div className={Style.filter_input}>
                          <input
                            type="number"
                            value={filter.value[0] || ''}
                            onChange={(e) => handleSalaryChange(index, 0, e.target.value)}
                            placeholder="Min Salary"
                          />
                        </div>
                        <div className={Style.filter_input}>
                          <input
                            type="number"
                            value={filter.value[1] || ''}
                            onChange={(e) => handleSalaryChange(index, 1, e.target.value)}
                            placeholder="Max Salary"
                          />
                        </div>
                      </div>
                    </>
                  ) :filter.field.includes('amount') ? (
                    <>
                      <div className={Style.filter_input}>
                        <select
                          value={filter.selectedOperator}
                          onChange={(e) => {
                            const updated = [...filters];
                            updated[index].selectedOperator = e.target.value;
                            if (e.target.value === 'between') {
                              updated[index].value = ['', '']; // reset for range inputs
                            } else {
                              updated[index].value = ''; // single value input
                            }
                            setFilters(updated);
                          }}
                        >
                          <option value="is" disabled>is</option>
                          <option value="contains" disabled>contains</option>
                          <option value="between">between</option>
                          <option value="start_with" disabled>Start with</option>
                          <option value="end_with" disabled>End with</option>
                        </select>
                      </div>
                      <div className={Style.date_range_wrapper}>
                        <div className={Style.filter_input}>
                          <input
                            type="number"
                            value={filter.value[0] || ''}
                            onChange={(e) => handleSalaryChange(index, 0, e.target.value)}
                            placeholder="Min Amount"
                          />
                        </div>
                        <div className={Style.filter_input}>
                          <input
                            type="number"
                            value={filter.value[1] || ''}
                            onChange={(e) => handleSalaryChange(index, 1, e.target.value)}
                            placeholder="Max Amount"
                          />
                        </div>
                      </div>
                    </>
                  ) : (filter.field === 'search_by_department' || filter.field.includes('department')) ? (
                    <>
                      <div className={Style.filter_input}>
                        <select
                          value={filter.selectedOperator}
                          onChange={(e) => {
                            const updated = [...filters];
                            updated[index].selectedOperator = e.target.value;
                            setFilters(updated);
                          }}
                        >
                          <option value="contains">contains</option>
                          <option value="is" disabled>is</option>
                          <option value="start_with" disabled>start with</option>
                          <option value="end_with" disabled>end with</option>
                        </select>
                      </div>

                      <div className={Style.filter_input}>
                        <select value={filter.value} onChange={(e) => handleInputChange(index, e)}>
                          <option value="" disabled>Select Department</option>
                          {
                            departmentList.map((els, ids) => {
                              return (
                                <option value={els?.title} key={ids}>{els?.title}</option>
                              )
                            })
                          }
                        </select>
                      </div>
                    </>
                  ) : filter.field === 'search_by_emp_type' ? (
                    <>
                      <div className={Style.filter_input}>
                        <select value={filter.value} onChange={(e) => handleInputChange(index, e)}>
                          <option value="" disabled>Select Employee Type</option>
                          <option value="all">All</option>
                          <option value="permanent">Permanent</option>
                          <option value="probation">Probation </option>
                        </select>
                      </div>
                    </>
                  ) : filter.field === 'search_by_emp_status' ? (
                    <>
                      <div className={Style.filter_input}>
                        <select value={filter.value} onChange={(e) => handleInputChange(index, e)}>
                          <option value="" selected disabled>Select Employee Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="all">All</option>
                        </select>
                      </div>
                    </>
                  ) : filter.field === 'search_by_joining_date' ? (
                    <>
                      <div className={Style.filter_input}>
                        <select
                          value={filter.selectedOperator || 'between'}
                          onChange={(e) => {
                            const updated = [...filters];
                            updated[index].selectedOperator = e.target.value;
                            if (e.target.value === 'between') {
                              updated[index].value = ['', ''];
                            } else if (e.target.value === 'is') {
                              updated[index].value = ''; // for single input
                            } else {
                              updated[index].value = e.target.value; // for 'today', etc.
                            }
                            setFilters(updated);
                          }}
                        >
                          <option value="is">is</option>
                          <option value="between" disabled>between</option>
                          <option value="today" disabled>today</option>
                          <option value="yesterday" disabled>yesterday</option>
                          <option value="this_month" disabled>this_month</option>
                          <option value="pervious_month" disabled>pervious_month</option>
                          <option value="this_year" disabled>this_year</option>
                        </select>
                      </div>

                      {/* If operator is "between", show range */}
                      {filter.selectedOperator === 'between' && (
                        <div className={Style.date_range_wrapper}>
                          <div className={Style.filter_input}>
                            <input
                              type="date"
                              value={filter.value?.[0] ? moment(filter.value?.[0], "DD-MM-YYYY").format("YYYY-MM-DD") : ''}
                              onChange={(e) => {
                                const formatted = moment(e.target.value).format("DD-MM-YYYY")
                                const updated = [...filters];
                                updated[index].value[0] = formatted;
                                setFilters(updated);
                              }}
                              placeholder="Start Date"
                            />
                          </div>

                          <div className={Style.filter_input}>
                            <input
                              type="date"
                              value={filter.value?.[1] ? moment(filter.value?.[1], "DD-MM-YYYY").format("YYYY-MM-DD") : ''}
                              onChange={(e) => {
                                const formatted = moment(e.target.value).format("DD-MM-YYYY")
                                const updated = [...filters];
                                updated[index].value[1] = formatted;
                                setFilters(updated);
                              }}
                              placeholder="End Date"
                            />
                          </div>
                        </div>
                      )}

                      {/* If operator is "is", show single date input */}
                      {filter.selectedOperator === 'is' && (
                        <div className={Style.filter_input}>
                          <input
                            type="date"
                            value={filter.value ? moment(filter.value, "DD-MM-YYYY").format("YYYY-MM-DD") : ''}
                            onChange={(e) => {
                              const formatted = moment(e.target.value).format("DD-MM-YYYY")
                              const updated = [...filters];
                              updated[index].value = formatted;
                              setFilters(updated);
                            }}
                            placeholder="Select Date"
                          />
                        </div>
                      )}
                    </>
                  ) : filter.field === 'month_year' ? (
                    <>
                      {/* If operator is "is", show single date input */}
                      <div className={Style.filter_input}>
                        <input
                          type="month"
                          value={filter.value ? moment(filter.value, "MM-YYYY").format("YYYY-MM") : ''}
                          onChange={(e) => {
                            const formatted = moment(e.target.value).format("MM-YYYY")
                            const updated = [...filters];
                            updated[index].value = formatted;
                            setFilters(updated);
                          }}
                          placeholder="Select Date"
                        />
                      </div>
                    </>
                  ) : filter.field === 'status' ? (
                    <>
                      {/* If operator is "is", show single date input */}
                      <div className={Style.filter_input}>
                        <select
                          value={filter.selectedOperator}
                          onChange={(e) => {
                            const updated = [...filters];
                            updated[index].selectedOperator = e.target.value;
                            setFilters(updated);
                          }}
                        >
                          <option value="is">is</option>
                          <option value="contains" disabled>contains</option>
                          <option value="start_with" disabled>Start with</option>
                          <option value="end_with" disabled>End with</option>
                        </select>
                      </div>

                      <div className={Style.filter_input}>
                        <select value={filter.value} onChange={(e) => handleInputChange(index, e)}>
                          <option value="" disabled>Select Status</option>
                          <option value={'at risk'}>At Risk</option>
                          <option value={'ok'}>Ok</option>
                        </select>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={Style.filter_input}>
                        <select
                          value={filter.selectedOperator}
                          onChange={(e) => {
                            const updated = [...filters];
                            updated[index].selectedOperator = e.target.value;
                            setFilters(updated);
                          }}
                        >
                          <option value="is" disabled>is</option>
                          <option value="contains">contains</option>
                          <option value="start_with" disabled>Start with</option>
                          <option value="end_with" disabled>End with</option>
                        </select>
                      </div>
                      <div className={Style.filter_input}>
                        <input
                          type={filter.field.includes("date") ? "date" : "text"}
                          value={filter.value ? filter.field.includes("date") ? moment(filter.value, "DD-MM-YYYY").format("YYYY-MM-DD") : filter.value : ''} //filter.value ? moment(filter.value, "DD-MM-YYYY").format("YYYY-MM-DD") : ''
                          onChange={(e) => {
                            const val = e.target.value;
                            const isDateField = filter.field.includes("date");
                            const formatted = isDateField && /^\d{4}-\d{2}-\d{2}$/.test(val)
                              ? moment(e.target.value).format("DD-MM-YYYY")
                              : val;
                            handleInputChange(index, { target: { value: formatted } });
                          }}
                          // onChange={(e) => handleInputChange(index, e)}
                          placeholder={`Enter ${filter.field.replace(/_/g, ' ')}`}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )
        ))}
      </div>

      <div className={Style.modal_footer}>
        <div className={Style.footer_buttons}>
          <button
            className={`${Style.btn} ${Style.w_100}`}
            onClick={handleSubmit}
            disabled={
              filters.filter(f =>
                f.isChecked && (
                  f.field === 'search_by_salary'
                    ? f.value[0] || f.value[1]
                    : f.value
                )
              ).length === 0
            }
          >
            Filter
          </button>

          {returnData?.length > 0 && (
            <button
              className={`${Style.btn} ${Style.outline}`}
              onClick={() => {
                const reset = filters.map(f => ({
                  ...f,
                  isChecked: false,
                  value:
                    f.field === 'search_by_salary' || f.field === 'search_by_joining_date'
                      ? ['', '']
                      : '',
                  selectedOperator:
                    f.field === 'search_by_salary'
                      ? 'between'
                      : f.field === 'search_by_joining_date'
                        ? 'is'
                        : 'contains',
                }));
                setFilters(reset);
                setSaveFilterName("My Filter")
                onSave([]); // Clear filters in parent
                closeFun(); // Optional: Close popup after clearing
              }}
            >
              Clear All
            </button>
          )}
        </div>
      </div>
    </LeftSidePopupLayout>
  );
};

export default FilterTabsPopup;
